import { ChevronDown, Search } from 'lucide-react';
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';

import {
  getWorldCountryByName,
  isoToFlag,
  searchWorldCountries,
  type WorldCountry,
} from '../../data/worldCountries';

type CountryAutocompleteProps = {
  id?: string;
  label?: React.ReactNode;
  value: string;
  onChange: (countryName: string, iso2: string) => void;
  disabled?: boolean;
  prefilled?: boolean;
  placeholder?: string;
  className?: string;
};

const CountryAutocomplete: React.FC<CountryAutocompleteProps> = ({
  id,
  label,
  value,
  onChange,
  disabled = false,
  prefilled = false,
  placeholder = 'Search country…',
  className = '',
}) => {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const selected = useMemo(() => getWorldCountryByName(value), [value]);
  const results = useMemo(
    () => searchWorldCountries(open ? query : value, 80),
    [open, query, value],
  );

  useEffect(() => {
    if (!open) setQuery(value);
  }, [value, open]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const pick = (country: WorldCountry) => {
    onChange(country.name, country.iso2);
    setQuery(country.name);
    setOpen(false);
  };

  const displayValue = open ? query : (selected?.name || value || '');

  return (
    <div className={`country-autocomplete ${className}`.trim()} ref={rootRef}>
      {label ? <label htmlFor={id} className="country-autocomplete__label">{label}</label> : null}
      <div className="country-autocomplete__control">
        <span className="country-autocomplete__flag" aria-hidden>
          {isoToFlag(selected?.iso2 || 'IN')}
        </span>
        <input
          ref={inputRef}
          id={id}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          autoComplete="country-name"
          disabled={disabled}
          value={displayValue}
          placeholder={placeholder}
          className={[
            'country-autocomplete__input',
            'credit-checkout-input',
            'touch-manipulation',
            prefilled ? 'credit-checkout-input--prefilled' : '',
          ].filter(Boolean).join(' ')}
          onFocus={() => { setOpen(true); setQuery(selected?.name || value || ''); }}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setOpen(false);
            if (e.key === 'ArrowDown') setOpen(true);
          }}
        />
        <ChevronDown className="country-autocomplete__chevron" aria-hidden />
      </div>
      {open && (
        <div id={listId} className="country-autocomplete__menu" role="listbox">
          <div className="country-autocomplete__search">
            <Search className="country-autocomplete__search-icon" aria-hidden />
            <input
              type="text"
              value={query}
              placeholder="Type country name or code…"
              className="country-autocomplete__search-input"
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
          </div>
          <ul className="country-autocomplete__list">
            {results.length === 0 ? (
              <li className="country-autocomplete__empty">No countries found</li>
            ) : results.map((country) => (
              <li key={country.iso2} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={country.name === value}
                  className={`country-autocomplete__option${
                    country.name === value ? ' country-autocomplete__option--active' : ''
                  }`}
                  onMouseDown={(e) => { e.preventDefault(); pick(country); }}
                >
                  <span className="country-autocomplete__option-flag" aria-hidden>
                    {isoToFlag(country.iso2)}
                  </span>
                  <span className="country-autocomplete__option-name">{country.name}</span>
                  <span className="country-autocomplete__option-meta">{country.iso2}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CountryAutocomplete;
